import {
  HHButton,
  HHLoadingButton,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, InputAdornment } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import { PaymentType } from '../../../types';
import { getCurrencySymbol } from '../../../utils/currency-helpers';
import {
  DEFAULT_ESI_FEE_SCHEDULE_FORM_VALUES,
  esiFeeScheduleFormSchema,
  EsiFeeScheduleFormValues,
} from './validation-schema';

export const PROGRAM_OPTIONS = [
  '',
  'acute',
  'balance',
  'chronic',
  'perisurgical',
];

const getProgramLabel = (option: string): string =>
  option === '' ? '(no program)' : option;

export const ESI_FEE_SCHEDULE_FORM_TEST_ID = 'esi-fee-schedule-form';
export const ESI_FEE_SCHEDULE_FORM_SUBMIT_TEST_ID =
  'esi-fee-schedule-form-submit-button';
export const ESI_FEE_SCHEDULE_FORM_CANCEL_TEST_ID =
  'esi-fee-schedule-form-cancel-button';
export const ESI_FEE_SCHEDULE_FORM_PROGRAM_INPUT_TEST_ID =
  'esi-fee-schedule-form-program-input';
export const ESI_FEE_SCHEDULE_FORM_PAYMENT_TYPE_INPUT_TEST_ID =
  'esi-fee-schedule-form-payment-type-input';
export const ESI_FEE_SCHEDULE_FORM_PRICE_INPUT_TEST_ID =
  'esi-fee-schedule-form-price-input';
export const ESI_FEE_SCHEDULE_FORM_UPC_INPUT_TEST_ID =
  'esi-fee-schedule-form-upc-input';
export const ESI_FEE_SCHEDULE_FORM_DESCRIPTION_INPUT_TEST_ID =
  'esi-fee-schedule-form-description-input';

interface EsiFeeScheduleFormProps {
  onSubmit: (values: EsiFeeScheduleFormValues) => void | Promise<void>;
  initialValues?: EsiFeeScheduleFormValues;
  paymentTypes: PaymentType[];
  paymentTypesLoading: boolean;
}

export const EsiFeeScheduleForm = ({
  onSubmit,
  initialValues,
  paymentTypes,
  paymentTypesLoading,
}: EsiFeeScheduleFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();

  const sortedPaymentTypes = [...paymentTypes].sort((a, b) =>
    a.code.localeCompare(b.code),
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
    reset,
  } = useForm<EsiFeeScheduleFormValues>({
    mode: 'onChange',
    resolver: yupResolver(esiFeeScheduleFormSchema),
    defaultValues: DEFAULT_ESI_FEE_SCHEDULE_FORM_VALUES,
  });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (
    values: EsiFeeScheduleFormValues,
  ): Promise<void> => {
    await onSubmit(values);
  };

  return (
    <form data-testid={ESI_FEE_SCHEDULE_FORM_TEST_ID}>
      <HHStack spacing={6} direction="column">
        <Controller
          control={control}
          name="program"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              value={value}
              options={PROGRAM_OPTIONS}
              getOptionLabel={getProgramLabel}
              isOptionEqualToValue={(option, val): boolean => option === val}
              onChange={(_, newValue): void => onChange(newValue ?? '')}
              disableClearable
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  label="Program"
                  inputProps={{ ...params.inputProps, name: 'program' }}
                  data-testid={ESI_FEE_SCHEDULE_FORM_PROGRAM_INPUT_TEST_ID}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="paymentType"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              value={sortedPaymentTypes.find(pt => pt.code === value) ?? null}
              options={sortedPaymentTypes}
              getOptionLabel={(option): string => option.code}
              isOptionEqualToValue={(option, val): boolean =>
                option.code === val?.code
              }
              onChange={(_, newValue): void => onChange(newValue?.code ?? '')}
              loading={paymentTypesLoading}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  label="Payment Type"
                  error={!!errors.paymentType}
                  helperText={errors.paymentType?.message}
                  inputProps={{ ...params.inputProps, name: 'paymentType' }}
                  data-testid={ESI_FEE_SCHEDULE_FORM_PAYMENT_TYPE_INPUT_TEST_ID}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="price"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Price"
              type="number"
              value={value ?? ''}
              onChange={onChange}
              error={!!errors.price}
              helperText={errors.price?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    disableTypography
                    sx={{ color: 'gray' }}
                  >
                    {getCurrencySymbol()}
                  </InputAdornment>
                ),
              }}
              inputProps={{ role: 'spinbutton', name: 'price' }}
              data-testid={ESI_FEE_SCHEDULE_FORM_PRICE_INPUT_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="upc"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="UPC"
              value={value}
              onChange={onChange}
              error={!!errors.upc}
              helperText={errors.upc?.message}
              inputProps={{ role: 'textbox', name: 'upc' }}
              data-testid={ESI_FEE_SCHEDULE_FORM_UPC_INPUT_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Description"
              value={value}
              onChange={onChange}
              error={!!errors.description}
              helperText={errors.description?.message}
              multiline
              rows={3}
              inputProps={{ role: 'textbox', name: 'description' }}
              data-testid={ESI_FEE_SCHEDULE_FORM_DESCRIPTION_INPUT_TEST_ID}
            />
          )}
        />

        <HHStack direction="row" spacing={2} justifyContent="flex-end">
          <HHButton
            hhVariant="variant-bypass"
            variant="text"
            onClick={handleCancel}
            type="button"
            disabled={isSubmitting}
            data-testid={ESI_FEE_SCHEDULE_FORM_CANCEL_TEST_ID}
          >
            Cancel
          </HHButton>
          <HHLoadingButton
            hhVariant="variant-bypass"
            variant="contained"
            type="submit"
            disabled={!isValid || !isDirty || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit(handleFormSubmit)}
            data-testid={ESI_FEE_SCHEDULE_FORM_SUBMIT_TEST_ID}
          >
            Submit
          </HHLoadingButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
